import { create } from 'zustand'
import {
  DEFAULT_AI_PROVIDER,
  getDefaultTextModel,
  getTextModelOption,
  isAIProviderId,
  isTextModelSelectionId,
  type AIProviderId,
  type TextModelSelectionId,
} from '@creanizer/types'
import {
  getApiKeyProvider,
  indexedDBManager,
  type ApiKey,
} from '@creanizer/utils'
import { createApiKeyActions } from './settingsApiKeyActions'
import { migrateLegacyApiKey } from './settingsMigration'
import type { SettingsState } from './settingsStoreTypes'

const PROVIDER_SETTING = 'ai-provider'
const modelSetting = (provider: AIProviderId) => `ai-model:${provider}`

function normalizeKeys(apiKeys: ApiKey[]): ApiKey[] {
  return apiKeys.map(key => ({
    ...key,
    provider: getApiKeyProvider(key),
  }))
}

async function getSavedModel(
  provider: AIProviderId
): Promise<TextModelSelectionId> {
  const saved = await indexedDBManager.getSetting(modelSetting(provider))
  return isTextModelSelectionId(saved) &&
    getTextModelOption(saved).provider === provider
    ? saved
    : getDefaultTextModel('name', provider)
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  apiKeys: [],
  activeApiKey: null,
  selectedProvider: DEFAULT_AI_PROVIDER,
  selectedModelId: getDefaultTextModel('name'),
  isLoading: true,

  ...createApiKeyActions(set, get),

  init: async () => {
    try {
      set({ isLoading: true })
      await indexedDBManager.init()
      await get().migrateFromLocalStorage()

      const savedProvider = await indexedDBManager.getSetting(PROVIDER_SETTING)
      const selectedProvider = isAIProviderId(savedProvider)
        ? savedProvider
        : DEFAULT_AI_PROVIDER
      const selectedModelId = await getSavedModel(selectedProvider)
      const apiKeys = normalizeKeys(await indexedDBManager.getApiKeys())
      let activeApiKey = await indexedDBManager.getActiveApiKey(selectedProvider)

      if (!activeApiKey) {
        activeApiKey = apiKeys.find(key =>
          getApiKeyProvider(key) === selectedProvider
        ) ?? null
        if (activeApiKey) {
          await indexedDBManager.setActiveApiKey(activeApiKey.id)
          activeApiKey = { ...activeApiKey, isActive: true }
        }
      }

      set({
        apiKeys,
        activeApiKey,
        selectedProvider,
        selectedModelId,
        isLoading: false,
      })
    } catch (error) {
      console.error('Failed to initialize settings:', error)
      set({ isLoading: false })
    }
  },

  setSelectedProvider: async provider => {
    const selectedModelId = await getSavedModel(provider)
    let activeApiKey = await indexedDBManager.getActiveApiKey(provider)
    if (!activeApiKey) {
      activeApiKey = get().getApiKeysForProvider(provider)[0] ?? null
      if (activeApiKey) {
        await indexedDBManager.setActiveApiKey(activeApiKey.id)
        activeApiKey = { ...activeApiKey, isActive: true }
      }
    }
    await indexedDBManager.setSetting(PROVIDER_SETTING, provider)
    set(state => ({
      selectedProvider: provider,
      selectedModelId,
      activeApiKey,
      apiKeys: state.apiKeys.map(key =>
        getApiKeyProvider(key) === provider
          ? { ...key, isActive: key.id === activeApiKey?.id }
          : key
      ),
    }))
  },

  setSelectedModel: async modelId => {
    const option = getTextModelOption(modelId)
    if (option.provider !== get().selectedProvider) {
      throw new Error('選択中のプロバイダーでは利用できないモデルです')
    }
    await indexedDBManager.setSetting(
      modelSetting(get().selectedProvider),
      modelId
    )
    set({ selectedModelId: modelId })
  },

  migrateFromLocalStorage: async () => {
    await migrateLegacyApiKey((name, key) => get().addApiKey(
      name,
      key,
      'gemini'
    ))
  },
}))
